/**
 * Back office → Support (D-129): the support centre. Requests from every channel with their
 * deadlines (first answer, resolution, complaint in business days), views (mine, unassigned,
 * overdue, complaints…), search, a new request entered by the team, the dashboard, saved replies,
 * the internal knowledge base and the refund requests to approve.
 */

import "./support.css";

import {
  type SupportTicket,
  type SupportTicketCategoryEnum,
  type SupportTicketChannelEnum,
  type SupportTicketPriorityEnum,
  type SupportTicketsListCategory,
  type SupportTicketsListPriority,
  useSupportTicketsCreate,
  useSupportTicketsList,
  useSupportTicketsSummaryRetrieve,
} from "@agencyos/api-client";
import { priorityTone, statusTone } from "@agencyos/core";
import { Badge, type BadgeTone, Button, EmptyState, GlassCard, SelectField, Skeleton, TextAreaField, TextField } from "@agencyos/ui-web";
import { LifeBuoy } from "lucide-react";
import { type FormEvent, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate, useSearchParams } from "react-router-dom";

import { Page } from "../../components/AppShell";
import { fieldErrors } from "../../lib/settings";
import { dateTimeText, Pager } from "../billing/shared";
import { ArticlesTab, MacrosTab } from "./KnowledgeTabs";
import { CustomerSearch } from "./CustomerSearch";
import { DashboardTab } from "./DashboardTab";
import { RefundsTab } from "./RefundsTab";
import { CATEGORIES, PRIORITIES, STAFF_CHANNELS, TimerBadge, useRefreshSupport, useSupportPermissions } from "./shared";

const VIEWS = ["mine", "unassigned", "overdue", "complaints", "waiting", "open", "resolved", "all"] as const;
type View = (typeof VIEWS)[number];
type Tab = "tickets" | "customers" | "dashboard" | "macros" | "articles" | "refunds";

function TicketRows({ rows }: { rows: SupportTicket[] }) {
  const { t, i18n } = useTranslation();
  return (
    <div className="table-wrap">
      <table className="table support-table">
        <thead>
          <tr>
            <th scope="col">{t("support.list.number")}</th>
            <th scope="col">{t("support.list.subject")}</th>
            <th scope="col">{t("support.list.requester")}</th>
            <th scope="col">{t("support.list.priority")}</th>
            <th scope="col">{t("support.list.status")}</th>
            <th scope="col">{t("support.list.deadline")}</th>
            <th scope="col">{t("support.list.assignee")}</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.id}>
              <td>
                <Link to={`/support/tickets/${row.id}`}>{row.number}</Link>
                <br />
                <small className="muted">{dateTimeText(row.created_at, i18n.language)}</small>
              </td>
              <td>
                <Link to={`/support/tickets/${row.id}`}>{row.subject}</Link>
                <div className="row support-chips">
                  <Badge>{t(`support.category.${row.category}`)}</Badge>
                  {row.is_complaint && row.category !== "complaint" ? <Badge tone="danger">{t("support.list.complaint")}</Badge> : null}
                  {(row.escalation_level ?? 0) > 0 ? <Badge tone="warning">{t("support.list.escalated", { level: row.escalation_level })}</Badge> : null}
                </div>
              </td>
              <td>
                {row.requester_name || "—"}
                <br />
                <small className="muted">{t(`support.channel.${row.channel}`)}</small>
              </td>
              <td>
                <Badge tone={priorityTone(row.priority ?? "normal") as BadgeTone}>{t(`support.priority.${row.priority ?? "normal"}`)}</Badge>
              </td>
              <td>
                <Badge tone={statusTone(row.status ?? "new") as BadgeTone}>{t(`support.status.${row.status ?? "new"}`)}</Badge>
              </td>
              <td>
                <TimerBadge timer={row.timer} />
              </td>
              <td>{row.assignee_name || <span className="muted">{t("support.list.nobody")}</span>}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function NewTicket({ onClose }: { onClose: () => void }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const refresh = useRefreshSupport();
  const create = useSupportTicketsCreate();
  const [form, setForm] = useState({
    subject: "",
    body: "",
    channel: "phone",
    category: "access",
    priority: "",
    language: "fr",
    requester_name: "",
    requester_email: "",
    requester_phone: "",
  });
  const errors = fieldErrors(create.error);
  const set = (key: keyof typeof form) => (value: string) => setForm({ ...form, [key]: value });
  const onSubmit = (event: FormEvent) => {
    event.preventDefault();
    create.mutate(
      {
        data: {
          subject: form.subject.trim(),
          body: form.body.trim(),
          channel: form.channel as SupportTicketChannelEnum,
          category: form.category as SupportTicketCategoryEnum,
          ...(form.priority ? { priority: form.priority as SupportTicketPriorityEnum } : {}),
          language: form.language === "en" ? "en" : "fr",
          requester_name: form.requester_name.trim(),
          requester_email: form.requester_email.trim(),
          requester_phone: form.requester_phone.trim(),
        },
      },
      {
        onSuccess: (response) => {
          if (response.status !== 201) return;
          refresh();
          navigate(`/support/tickets/${response.data.id}`);
        },
      },
    );
  };
  return (
    <GlassCard as="section" className="stack" aria-labelledby="support-new-title">
      <h2 id="support-new-title">{t("support.new.title")}</h2>
      <p className="muted">{t("support.new.intro")}</p>
      <form className="stack" onSubmit={onSubmit} noValidate aria-label={t("support.new.title")}>
        <div className="form-grid">
          <TextField label={t("support.new.name")} value={form.requester_name} onChange={(e) => set("requester_name")(e.target.value)} error={errors.requester_name} />
          <TextField type="email" label={t("support.new.email")} value={form.requester_email} onChange={(e) => set("requester_email")(e.target.value)} error={errors.requester_email} />
          <TextField type="tel" label={t("support.new.phone")} value={form.requester_phone} onChange={(e) => set("requester_phone")(e.target.value)} error={errors.requester_phone} />
          <SelectField label={t("support.new.language")} value={form.language} onChange={(e) => set("language")(e.target.value)} options={[{ value: "fr", label: "Français" }, { value: "en", label: "English" }]} />
          <SelectField label={t("support.new.channel")} value={form.channel} onChange={(e) => set("channel")(e.target.value)} options={STAFF_CHANNELS.map((c) => ({ value: c, label: t(`support.channel.${c}`) }))} />
          <SelectField label={t("support.new.category")} value={form.category} onChange={(e) => set("category")(e.target.value)} options={CATEGORIES.map((c) => ({ value: c, label: t(`support.category.${c}`) }))} />
          <SelectField
            label={t("support.new.priority")}
            value={form.priority}
            onChange={(e) => set("priority")(e.target.value)}
            options={[{ value: "", label: t("support.new.priorityAuto") }, ...PRIORITIES.map((p) => ({ value: p, label: t(`support.priority.${p}`) }))]}
          />
        </div>
        <TextField label={t("support.new.subject")} value={form.subject} maxLength={200} required onChange={(e) => set("subject")(e.target.value)} error={errors.subject} />
        <TextAreaField label={t("support.new.body")} value={form.body} rows={5} required onChange={(e) => set("body")(e.target.value)} error={errors.body ?? errors.detail} />
        <div className="row">
          <Button type="submit" disabled={create.isPending || !form.subject.trim() || !form.body.trim()}>
            {create.isPending ? t("common.loading") : t("support.new.create")}
          </Button>
          <Button type="button" variant="ghost" onClick={onClose}>
            {t("support.new.cancel")}
          </Button>
        </div>
      </form>
    </GlassCard>
  );
}

function TicketsTab() {
  const { t } = useTranslation();
  const [params, setParams] = useSearchParams();
  const view = (VIEWS as readonly string[]).includes(params.get("view") ?? "") ? (params.get("view") as View) : "open";
  const [q, setQ] = useState("");
  const [category, setCategory] = useState("");
  const [priority, setPriority] = useState("");
  const [page, setPage] = useState(1);
  const summary = useSupportTicketsSummaryRetrieve();
  const counts: Partial<Record<View, number>> | undefined = summary.data?.status === 200 ? summary.data.data : undefined;
  const list = useSupportTicketsList({
    view,
    page,
    ...(q.trim() ? { q: q.trim() } : {}),
    ...(category ? { category: category as SupportTicketsListCategory } : {}),
    ...(priority ? { priority: priority as SupportTicketsListPriority } : {}),
  });
  const data = list.data?.status === 200 ? list.data.data : undefined;
  let content;
  if (list.isPending) content = <Skeleton style={{ height: 220 }} />;
  else if (!data) content = <p role="alert">{t("common.error")}</p>;
  else if (!data.results.length) content = <EmptyState compact icon={<LifeBuoy aria-hidden="true" />} title={t("support.list.empty")} />;
  else
    content = (
      <>
        <TicketRows rows={data.results} />
        <Pager page={page} hasNext={Boolean(data.next)} onChange={setPage} labels={{ previous: t("notifications.log.previous"), next: t("notifications.log.next"), page: t("notifications.log.page", { page }) }} />
      </>
    );
  return (
    <div className="stack">
      <div className="tabs" role="group" aria-label={t("support.tabs.tickets")}>
        {VIEWS.map((key) => (
          <button
            key={key}
            type="button"
            className={`tabs__tab${view === key ? " active" : ""}`}
            aria-pressed={view === key}
            onClick={() => {
              setPage(1);
              setParams((current) => {
                const next = new URLSearchParams(current);
                next.set("view", key);
                return next;
              });
            }}
          >
            {t(`support.views.${key}`)}
            {counts ? <Badge tone={key === "overdue" && (counts[key] ?? 0) > 0 ? "danger" : "default"}>{counts[key] ?? 0}</Badge> : null}
          </button>
        ))}
      </div>
      <div className="form-grid">
        <TextField label={t("support.list.search")} value={q} onChange={(e) => (setQ(e.target.value), setPage(1))} type="search" />
        <SelectField label={t("support.list.category")} value={category} onChange={(e) => (setCategory(e.target.value), setPage(1))} options={[{ value: "", label: t("support.list.anyCategory") }, ...CATEGORIES.map((c) => ({ value: c, label: t(`support.category.${c}`) }))]} />
        <SelectField label={t("support.list.priority")} value={priority} onChange={(e) => (setPriority(e.target.value), setPage(1))} options={[{ value: "", label: t("support.list.anyPriority") }, ...PRIORITIES.map((p) => ({ value: p, label: t(`support.priority.${p}`) }))]} />
      </div>
      <GlassCard as="section" className="stack" aria-label={t(`support.views.${view}`)}>
        {content}
      </GlassCard>
    </div>
  );
}

export function SupportPage() {
  const { t } = useTranslation();
  const can = useSupportPermissions();
  const [creating, setCreating] = useState(false);
  const [params, setParams] = useSearchParams();
  const tabs: Tab[] = ["tickets"];
  if (can("support.customers.read")) tabs.push("customers");
  if (can("support.dashboard")) tabs.push("dashboard");
  tabs.push("macros", "articles");
  if (can("support.refunds.request") || can("support.refunds.decide")) tabs.push("refunds");
  const asked = params.get("tab") as Tab | null;
  const tab: Tab = asked && tabs.includes(asked) ? asked : "tickets";
  return (
    <Page
      title={t("support.title")}
      description={t("support.intro")}
      actions={
        can("support.tickets.create") ? (
          <Button onClick={() => setCreating(true)} disabled={creating}>
            {t("support.new.button")}
          </Button>
        ) : null
      }
    >
      {creating ? <NewTicket onClose={() => setCreating(false)} /> : null}
      <div className="tabs" role="tablist" aria-label={t("support.title")}>
        {tabs.map((key) => (
          <button
            key={key}
            type="button"
            role="tab"
            id={`support-tab-${key}`}
            aria-selected={tab === key}
            aria-controls={`support-panel-${key}`}
            className="tabs__tab"
            onClick={() => setParams(new URLSearchParams({ tab: key }))}
          >
            {key === "customers" ? t("support.card.search") : t(`support.tabs.${key}`)}
          </button>
        ))}
      </div>
      <div role="tabpanel" id={`support-panel-${tab}`} aria-labelledby={`support-tab-${tab}`} className="stack">
        {tab === "tickets" ? <TicketsTab /> : null}
        {tab === "customers" ? <CustomerSearch /> : null}
        {tab === "dashboard" ? <DashboardTab /> : null}
        {tab === "macros" ? <MacrosTab /> : null}
        {tab === "articles" ? <ArticlesTab /> : null}
        {tab === "refunds" ? <RefundsTab /> : null}
      </div>
    </Page>
  );
}
